import './i18n';
// normalize is imported from globals.css via `@import ... layer(base)` so Tailwind utilities can
// override it; importing it here (unlayered) would let its resets outrank every utility.
import './globals.css';
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/browser';
import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter } from 'react-router-dom';
import { Loader } from '@ui';
import { TooltipProvider } from '@ui/components/ui/tooltip';
import TagManager from 'react-gtm-module';
import PanencoBot from './components';
import NotificationProvider from './utils/notification-provider';
import AuthProvider from './utils/autorized-provider';
import { ThemeProvider } from './utils/theme-provider';
import { closeIfConnectPopup } from './components/core/philBot/returnTrip';
import ConnectPopupDone from './components/core/philBot/ConnectPopupDone';

const rootElement = createRoot(document.getElementById('root') as HTMLElement);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: () => false,
    },
  },
});

if (import.meta.env.MODE && ['production', 'staging'].includes(import.meta.env.MODE)) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
  });
  TagManager.initialize({ gtmId: import.meta.env.VITE_GTM_ID });
}

const App = () => (
  <ThemeProvider defaultTheme="light">
    <div className="flex min-h-dvh w-full flex-1 flex-col">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider delayDuration={300}>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AuthProvider>
              <Suspense fallback={<Loader />}>
                <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID}>
                  <PanencoBot />
                  <NotificationProvider />
                </GoogleOAuthProvider>
              </Suspense>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
        {import.meta.env.VITE_REACT_QUERY_DEVTOOLS === 'true' ? (
          <ReactQueryDevtools initialIsOpen={false} position="bottom" />
        ) : null}
      </QueryClientProvider>
    </div>
  </ThemeProvider>
);

rootElement.render(
  closeIfConnectPopup() ? (
    <Suspense fallback={null}>
      <ConnectPopupDone />
    </Suspense>
  ) : (
    <App />
  ),
);
